// Single source for everything a search engine, an answer engine or a social
// card needs to know about this studio.
//
// Imported by three consumers, which is the whole point of it living here:
//   - src/components/Faq.jsx        renders the questions for humans
//   - scripts/build_seo.mjs         emits robots.txt, sitemap.xml, llms.txt
//                                   and injects JSON-LD into index.html
//   - src/App.jsx                   nothing yet, but the shape is stable
//
// Rules that apply here exactly as they do in the UI: no figures, and nothing
// invented. An answer engine repeats what it reads. A wrong fact here becomes
// a wrong fact stated confidently by ChatGPT to somebody deciding whether to
// trust this studio, and there is no correction mechanism once that happens.

import { STUDIO } from './studio.js';

// Set VITE_SITE_URL in the host's environment once the real domain is live.
// Everything canonical derives from this, so it only has to be right once.
// Read from Vite in the browser bundle, from process.env when the build
// script imports this same module under plain Node.
export const SITE_URL = (
  import.meta.env?.VITE_SITE_URL ||
  globalThis.process?.env?.VITE_SITE_URL ||
  'https://handmadebee.pages.dev'
).replace(/\/+$/, '');

export const SITE = {
  url: SITE_URL,
  name: STUDIO.name,
  tagline: STUDIO.tagline,
  locale: 'en_IN',
  // 155 chars or under: past that Google truncates it in the result.
  description:
    'Bespoke handcraft atelier in Manathala, Thrissur — Aari embroidery, fine ' +
    'art, custom designing, gift hampers, 3D keepsake frames and tailoring, ' +
    'made to order since 2010.',
  shortDescription: 'Bespoke handcraft atelier in Thrissur, Kerala. Made to order.',
  logo: '/logo-256.webp',
  socialImage: '/og-cover.jpg',

  // Google Search Console verification. Paste the value from the "HTML tag"
  // method here — just the content string, not the whole <meta> element —
  // then run `npm run seo` and redeploy.
  //
  // The DNS method is not available on a *.pages.dev address, because
  // Cloudflare owns that zone, not us. Once a real domain is in place, verify
  // by DNS instead: it covers every subdomain and survives redeploys.
  googleSiteVerification: null,
};

// The six crafts, in the order the atelier lists them. `name` is what a person
// would type into a search box; `blurb` is what an answer engine will quote.
export const SERVICES = [
  {
    id: 'embroidery',
    name: 'Aari embroidery',
    blurb:
      'Hand-worked Aari and zardosi on bridal blouses, borders and panels — ' +
      'cutdana, resham threadwork and zari, worked on a frame by hand.',
  },
  {
    id: 'painting',
    name: 'Fine art',
    blurb:
      'Original canvas work — oil, acrylic and resin pieces, including ' +
      'commissioned portraits worked from your own reference photographs.',
  },
  {
    id: 'designing',
    name: 'Custom designing',
    blurb:
      'Bespoke outfits designed from a brief and cut to your measurements, ' +
      'from first sketch through fabric selection to final fitting.',
  },
  {
    id: 'hampers',
    name: 'Gift hampers',
    blurb:
      'Assembled and hand-finished gift hampers for weddings, festivals and ' +
      'family occasions, put together around a theme you choose.',
  },
  {
    id: 'frames',
    name: '3D keepsake frames',
    blurb:
      'Shadow-box frames that preserve varmala flowers, invitations and ' +
      'wedding keepsakes in a sealed, mounted display.',
  },
  {
    id: 'tailoring',
    name: 'Master tailoring',
    blurb:
      'Precision tailoring and alterations — princess-cut blouses, linings ' +
      'and fittings finished to measure.',
  },
];

// Answer engines lift these close to verbatim. Every answer below is checkable
// against the site itself. Where the studio has not given a figure — lead
// times, years in business, location — the answer says how to find out rather
// than guessing, because a guess here is a promise somebody will hold you to.
export const FAQS = [
  {
    q: 'What does Handmade bee make?',
    a:
      'Handmade bee is a bespoke handcraft atelier working across six crafts: ' +
      'Aari embroidery, fine art on canvas, custom outfit designing, gift ' +
      'hampers, 3D keepsake frames and master tailoring. Everything is made ' +
      'to order rather than sold from stock.',
  },
  {
    q: 'Do you take custom orders?',
    a:
      'Yes — custom work is the only kind of work the atelier does. Every ' +
      'piece starts from a brief: what you want made, the occasion, any ' +
      'reference images you have, and your timeline.',
  },
  {
    q: 'How do I place an order?',
    a:
      'Use the order form on the site to describe the piece, or message the ' +
      `studio on WhatsApp at ${STUDIO.phoneDisplay}. The studio replies with ` +
      'questions about materials and measurements before anything is agreed.',
  },
  {
    q: 'How is a commissioned piece quoted?',
    a:
      'Each piece is quoted individually, because what it takes depends on ' +
      'the materials, the density of the handwork and the size of the piece. ' +
      'Send a brief through the order form and the studio quotes against that ' +
      'specific piece rather than against a standard list.',
  },
  {
    q: 'How long does a commissioned piece take?',
    a:
      'It depends on the craft and how much handwork the design carries — ' +
      'dense Aari embroidery takes considerably longer than an alteration. ' +
      'The studio confirms a timeline with you when the brief is agreed, ' +
      'before work starts.',
  },
  {
    q: 'Can you work from a photograph or a reference design?',
    a:
      'Yes. Reference photographs, sketches and saved images are the usual ' +
      'starting point for both embroidery and portrait commissions. Send them ' +
      'with your brief.',
  },
  {
    q: 'Where is Handmade bee based?',
    a:
      `The studio works from ${STUDIO.place.locality}, ${STUDIO.place.city} ` +
      `district, ${STUDIO.place.region}, and takes commissions from ` +
      `${STUDIO.place.areaServed.slice(0, -1).join(', ')} and across ` +
      `${STUDIO.place.region}. Message the studio on ` +
      `${STUDIO.phoneDisplay} to arrange a fitting or a visit.`,
  },
  {
    q: 'How long has the studio been working?',
    a:
      `Handmade bee has been taking commissions since ${STUDIO.since}. The ` +
      'Instagram account, where most of the finished work is posted, dates ' +
      'from 2022.',
  },
  {
    q: 'Do you do bridal work?',
    a:
      'Yes — bridal blouses in Aari and zardosi, bridal gift hampers and 3D ' +
      'frames that preserve varmala flowers after the wedding are all part of ' +
      'the regular catalogue.',
  },
];

export default SITE;
